import { CircleCheck } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { RadioGroupItem } from '@/components/ui-kit/radio-group';
import { LunchMenu } from '../../types/lunch.types';

export interface MealOptionCardProps {
  menu: LunchMenu;
  index: number;
  isSelected?: boolean;
  isOrdered?: boolean;
  onSelect?: () => void;
  showRadio?: boolean;
}

const TINT_DISC_CLASSES = [
  'border-tint-gold-border bg-tint-gold text-tint-gold-foreground',
  'border-tint-blue-border bg-tint-blue text-tint-blue-foreground',
  'border-tint-green-border bg-tint-green text-tint-green-foreground',
];

/**
 * Restaurant-style meal menu card with tinted disc number, radio selection,
 * numbered item list with restaurant-style dividers, and item count.
 * Matches HR Module Prototype `.meal-opt`.
 */
export const MealOptionCard = ({
  menu,
  index,
  isSelected = false,
  isOrdered = false,
  onSelect,
  showRadio = true,
}: MealOptionCardProps) => {
  const tintClass = TINT_DISC_CLASSES[index % TINT_DISC_CLASSES.length];
  const items = menu.MenuItems ?? [];

  return (
    <label
      onClick={onSelect}
      className={`group relative flex cursor-pointer flex-col gap-3.5 rounded-lg border bg-card p-5 shadow-sm transition-all duration-200 ${
        isSelected || isOrdered
          ? 'border-primary shadow-md ring-1 ring-primary'
          : 'border-border hover:border-neutral-400 hover:shadow-md dark:hover:border-neutral-600'
      }`}
    >
      {/* Header: Disc Number + Menu Name + Radio / Badge */}
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={`grid size-8 shrink-0 place-items-center rounded-full border text-sm font-semibold tabular-nums ${tintClass}`}
        >
          {index + 1}
        </span>

        <span className="text-base font-semibold text-foreground">
          {menu.Name}
        </span>

        <div className="ml-auto flex items-center">
          {isOrdered ? (
            <Badge
              variant="outline"
              className="gap-1 border-tint-green-border bg-tint-green font-medium text-tint-green-foreground"
            >
              <CircleCheck className="size-3.5" />
              Ordered
            </Badge>
          ) : showRadio ? (
            <RadioGroupItem
              value={menu.ItemId}
              checked={isSelected}
              className="pointer-events-none mt-0.5"
            />
          ) : null}
        </div>
      </div>

      {/* Item list: numbered items with restaurant-style subtle dividers */}
      <ol className="m-0 list-none p-0 text-sm leading-relaxed text-foreground">
        {items.map((item, itemIdx) => (
          <li
            key={itemIdx}
            className="flex items-baseline gap-2 border-t border-dashed border-border/80 py-2 first:border-t-0 first:pt-1 last:pb-1"
          >
            <span
              aria-hidden="true"
              className="w-4 shrink-0 text-xs font-medium text-muted-foreground/70"
            >
              {itemIdx + 1}
            </span>
            <span className="min-w-0 break-words">{item}</span>
          </li>
        ))}
      </ol>

      {/* Item count */}
      <span className="mt-auto pt-1 text-xs text-muted-foreground">
        {items.length} {items.length === 1 ? 'item' : 'items'}
      </span>
    </label>
  );
};
